import { getAvailablePricingsByAgentId, getPricingsByAgentId, Pricing } from '@nemuru-eng/js-api-client';
import { useMemo, useState } from 'react';

import { PricingData } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/types/PricingData';
import { useCache } from '@/hooks/requests/useCache';

interface UsePricingSelectorProps {
  agentId: string;
}

export const usePricingSelector = ({ agentId }: UsePricingSelectorProps) => {
  const { data: agentPricings, revalidate } = useCache(getPricingsByAgentId, { agentId });
  const { data: agentAvailablePricings } = useCache(getAvailablePricingsByAgentId, { agentId });

  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [availablePricings, setAvailablePricings] = useState<PricingData[]>();
  const [filteredPricings, setFilteredPricings] = useState<PricingData[]>();

  const setFilteredAvailablePricings = async (): Promise<void> => {
    if (!agentAvailablePricings) return;
    setLoading(true);
    const pricingsData = mapPricingsToPricingsData(agentAvailablePricings);
    setAvailablePricings(pricingsData);
    setFilteredPricings(pricingsData);
    setLoading(false);
  };

  const getAgentPricings = async (): Promise<void> => {
    setLoading(true);
    await revalidate();
    reset();
    setLoading(false);
  };

  const mapPricingsToPricingsData = (pricings?: Pricing[]): PricingData[] | undefined => {
    if (!pricings) return;
    return pricings.map(toPricingData);
  };

  const toPricingData = (pricing: Pricing): PricingData => {
    const belongsToAgent = agentPricings ? agentPricings.some((agentPricing) => agentPricing.id === pricing.id) : false;
    const pricingNamesInLenderContext = pricing.ranges.reduce((cum, range) => {
      if (!range.lenderEarningsFees) return cum;
      const additional: string[] = [];
      range.lenderEarningsFees.forEach((lenderEarningFee) => {
        const pricingNameInLenderContext = lenderEarningFee.pricingNameInLenderContext.toLowerCase();
        if (!cum.includes(pricingNameInLenderContext)) {
          additional.push(pricingNameInLenderContext);
        }
      });
      return [...cum, ...additional];
    }, [] as string[]);
    return {
      ...pricing,
      belongsToAgent,
      selected: belongsToAgent,
      pricingNamesInLenderContext,
      tagsArr: pricing.tags.map((tag) => tag.toLowerCase()),
    };
  };

  const searchAvailablePricing = (value: string): void => {
    const search = value.toLowerCase();
    if (!availablePricings) return;
    if (value === '') reset();
    const resultingPricings = availablePricings.filter((pricing) => {
      if (pricing.name.toLowerCase().includes(search)) return pricing;
      if (pricing.pricingNamesInLenderContext.join().includes(search)) return pricing;
      if (pricing.tagsArr.join().includes(search)) return pricing;
    });
    setFilteredPricings(resultingPricings);
    setPage(0);
  };

  const reset = (): void => setFilteredPricings(mapPricingsToPricingsData(agentAvailablePricings));

  const selectPricing = (pricingId: string): void => {
    setFilteredPricings(filteredPricings?.map((pricing) => toggleSelection(pricingId, pricing)));
    setAvailablePricings(availablePricings?.map((pricing) => toggleSelection(pricingId, pricing)));
  };

  const toggleSelection = (pricingId: string, pricing: PricingData): PricingData => {
    if (pricing.id !== pricingId) return pricing;
    return { ...pricing, selected: !pricing.selected };
  };

  const selectedPricings = useMemo(() => {
    if (!availablePricings) return [];
    return availablePricings.filter((pricing) => pricing.selected === true);
  }, [availablePricings]);

  return {
    loading,
    setLoading,
    page,
    setPage,
    filteredPricings,
    selectedPricings,
    agentPricings,
    setFilteredAvailablePricings,
    getAgentPricings,
    searchAvailablePricing,
    selectPricing,
  };
};
